import { useEffect, useRef, useState } from 'react';
import { Sparkles, RotateCw, CloudOff, Loader, FilePen } from 'lucide-react';
import AiReviewReport from '../../components/AiReviewReport';
import type { ManualRegistrationData } from './types';
import { filesSignature, runContractAiReview, type AiReviewState } from './aiReview';

export const MIN_JUSTIFICATION = 10;

const RUNNING_STEPS = [
  'Identificando cada documento',
  'Lendo holerite, carteira e comprovante',
  'Conferindo RG/CNH e selfie',
  'Procurando empresas de empréstimo no extrato',
  'Ouvindo o vídeo de confirmação',
  'Comparando com a ficha do cliente',
];

interface Props {
  data: ManualRegistrationData;
  contractTempId: string;
  review: AiReviewState | null;
  onReviewChange: (state: AiReviewState) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  onEditDocuments: () => void;
}

function RunningCard() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const stepIndex = Math.min(Math.floor(elapsed / 8), RUNNING_STEPS.length - 1);

  return (
    <div className="p-5 rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white">
      <div className="flex items-center gap-3">
        <div className="relative w-10 h-10 flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-sky-200 animate-ping opacity-40" />
          <span className="relative w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-sky-900">Analisando documentos…</p>
          <p className="text-xs text-sky-700 mt-0.5">Isso costuma levar cerca de 1 minuto ({elapsed}s)</p>
        </div>
      </div>
      <ul className="mt-4 space-y-2">
        {RUNNING_STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-2 text-xs transition-colors duration-300 ${i < stepIndex ? 'text-sky-700' : i === stepIndex ? 'text-sky-900 font-semibold' : 'text-gray-400'}`}>
            {i === stepIndex
              ? <Loader className="w-3.5 h-3.5 animate-spin" />
              : <span className={`w-1.5 h-1.5 mx-1 rounded-full ${i < stepIndex ? 'bg-sky-500' : 'bg-gray-300'}`} />}
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AiReviewPanel({ data, contractTempId, review, onReviewChange, justification, onJustificationChange, onEditDocuments }: Props) {
  const signature = filesSignature(data);
  const startedFor = useRef<string | null>(null);

  const start = async () => {
    startedFor.current = signature;
    onReviewChange({ signature, phase: 'running' });
    const result = await runContractAiReview(data, contractTempId);
    if (startedFor.current === signature) onReviewChange({ signature, ...result });
  };

  useEffect(() => {
    if (review?.signature === signature || startedFor.current === signature) return;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const header = (
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
      <Sparkles className="w-4 h-4 text-sky-600" />
      <span className="text-sm font-semibold text-gray-700 flex-1">Revisão automática dos documentos</span>
      {review && review.phase !== 'running' && (
        <button
          type="button"
          onClick={start}
          className="flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900 transition-colors"
        >
          <RotateCw className="w-3.5 h-3.5" /> Analisar novamente
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
      {header}
      <div className="p-4 space-y-4">
        {(!review || review.phase === 'running') && <RunningCard />}

        {review?.phase === 'failed' && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
            <CloudOff className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Revisão automática indisponível</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                {review.message} Você pode enviar o contrato normalmente; o gestor verá que esta venda não teve revisão automática.
              </p>
            </div>
          </div>
        )}

        {review?.phase === 'done' && (
          <>
            <AiReviewReport overall={review.overall} items={review.items} files={review.files} />
            {review.overall !== 'ok' && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-blue-200 bg-blue-50">
                <p className="flex-1 text-xs text-blue-900 leading-relaxed">
                  Anexou algo errado ou faltou algum documento? Corrija os arquivos e a revisão será feita de novo automaticamente.
                </p>
                <button
                  type="button"
                  onClick={onEditDocuments}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                >
                  <FilePen className="w-4 h-4" /> Corrigir documentos
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 leading-relaxed">
              A revisão é um apoio: confira os alertas antes de enviar.
            </p>
            {review.overall === 'problem' && (
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Justificativa para enviar mesmo assim <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={justification}
                  onChange={e => onJustificationChange(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Explique ao gestor por que o contrato deve seguir apesar dos problemas apontados."
                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-sky-500 focus:outline-none transition-colors resize-none"
                />
                {justification.trim().length < MIN_JUSTIFICATION && (
                  <p className="text-xs text-red-600 mt-1">Escreva ao menos {MIN_JUSTIFICATION} caracteres para liberar o envio.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
